import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useCallback, useContext, useState } from 'react';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import { getExpenseReportUrlById, updateExpenseReport } from '../../api/expenseReport';
// Default context values
const defaultContext = {
    selectedRows: [],
    setSelectedRows: () => {
        throw new Error('setSelectedRows is not implemented');
    },
    markAsExported: async () => {
        throw new Error('markAsExported is not implemented');
    },
    reSync: async () => {
        throw new Error('reSync is not implemented');
    },
    getExpenseReportUrl: () => {
        throw new Error('getExpenseReportUrl is not implemented');
    },
    catchError: () => {
        throw new Error('catchError is not implemented');
    }
};
const ERPGatewayExportContext = createContext(defaultContext);
export function ERPGatewayExportProvider({ children, getExportData, catchError, doNetSuiteExport }) {
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const [selectedRows, setSelectedRows] = useState([]);
    // Function to mark an expense report, or all selected exp rpts as exported
    const markAsExported = useCallback(async (id) => {
        const payload = {
            Id: id,
            APSyncComplete: true,
            GLSyncComplete: true
        };
        applyLoadingState(30000);
        try {
            if (id) {
                await updateExpenseReport(payload);
            }
            else {
                // eslint-disable-next-line no-restricted-syntax
                for (const row of selectedRows) {
                    // eslint-disable-next-line no-await-in-loop
                    await updateExpenseReport({ ...payload, Id: row });
                }
            }
            getExportData(); // Ensure this is awaited to avoid overlapping processes
        }
        catch (error) {
            catchError(error);
        }
        finally {
            removeLoadingState();
        }
    }, [applyLoadingState, removeLoadingState, getExportData, selectedRows, catchError]);
    // Function to re-sync a specific expense report or all selected exp rpts
    const reSync = useCallback(async (id) => {
        try {
            if (id) {
                doNetSuiteExport([id]);
            }
            else {
                doNetSuiteExport(selectedRows);
            }
        }
        catch (error) {
            catchError(error);
        }
    }, [catchError, doNetSuiteExport, selectedRows]);
    // Function to get the expense report URL for a specific expense report ID
    const getExpenseReportUrl = useCallback(async (id) => {
        let responseJSON = null;
        applyLoadingState(30000);
        try {
            const response = await getExpenseReportUrlById(id);
            responseJSON = response.json();
            removeLoadingState();
            return responseJSON;
        }
        catch (error) {
            catchError(error);
            removeLoadingState();
            return '';
        }
    }, [applyLoadingState, catchError, removeLoadingState]);
    const contextValue = React.useMemo(() => ({
        selectedRows,
        setSelectedRows,
        markAsExported,
        reSync,
        getExpenseReportUrl,
        catchError
    }), [selectedRows, setSelectedRows, markAsExported, reSync, getExpenseReportUrl, catchError]);
    return (_jsx(ERPGatewayExportContext.Provider, { value: contextValue, children: children }));
}
export const useERPGatewayExport = () => {
    const context = useContext(ERPGatewayExportContext);
    if (!context) {
        throw new Error('useERPGateway must be used within an Export');
    }
    return context;
};
